<template>
  <v-dialog :value="dialog" max-width="400" @click:outside="closeDialog">
    <v-card v-if="step !== `success`" class="dialog">
      <div class="dialog__head">
        <v-card-title class="dialog__head__title">
          {{ $t("practitioner.schedule.title") }}
        </v-card-title>
        <v-icon @click="closeDialog" size="small" class="dialog__head__close-icon">mdi-close</v-icon>
      </div>
      <Stepper :step="step" :texts="texts" className="dialog__stepper" />
      <div class="dialog__details">
        <v-row class="mb-1">
          <img src="@/assets/icons/calendar-blue.svg" />
          <span class="dialog__details__label">{{ $t("visits.schedule.second.date") }}:</span>
          <span class="dialog__details__text" @click="toggleDatepicker">{{ date }}</span>
          <v-date-picker
            v-if="datepickerOpen"
            ref="picker"
            reactive
            class="dialog__details__date-picker"
            :value="visitDate"
            @click:date="updateDate"
            color="primary"
            :no-title="true"
            :allowed-dates="allowedDates"
            :max="maxScheduleDate"
            :min="minScheduleDate"
          />
        </v-row>
        <v-row>
          <img src="@/assets/icons/time-blue.svg" />
          <span class="dialog__details__label">{{ $t("visits.schedule.second.time") }}:</span>
          <div class="dialog__details__select-block">
            <span class="dialog__details__text">{{ timeslot }}</span>
            <v-select
              class="dialog__details__select"
              :value="slotId"
              :placeholder="timeslot"
              @change="updateSlotId"
              :items="slots"
              dense
            ></v-select>
          </div>
        </v-row>
      </div>
      <span class="heading-5 p-schedule-visit__content__visit-reason__title mt-6">
        {{ $t("practitioner.schedule.choosePatient") }}
        <span class="p-schedule-visit__content__required">*</span>
      </span>
      <Select
        :label="$t('practitioner.schedule.choosePatientFromList')"
        :value="patientId"
        :hideDetails="false"
        :items="patientsOptions"
        @change="onPatientChange"
        class="dialog__select"
      />

      <span class="heading-5 p-schedule-visit__content__visit-reason__title">
        {{ $t("visits.chiefComplaint") }}
        <span class="p-schedule-visit__content__required">*</span>
      </span>
      <Select
        :label="$t('practitioner.schedule.choosePatientComplaint')"
        :value="chiefComplaint"
        :hideDetails="false"
        :items="chiefComplaints"
        @change="onChiefComplaintChange"
        class="dialog__select"
      />

      <span class="heading-5 p-schedule-visit__content__visit-reason__title">
        {{ $t("visits.schedule.first.describeProblem") }}
        <span class="p-schedule-visit__content__required">*</span>
      </span>
      <Textarea
        :value="complaintDescription"
        :placeholder="$t('visits.schedule.first.visitReason')"
        :height="67"
        :hideDetails="true"
        @change="onVisitReasonChange"
        class="dialog__select"
      />

      <div class="dialog__footer">
        <!-- <v-btn @click="submit" depressed dark color="primary" class="dialog__footer__btn">{{
          $t("visits.schedule.second.continue")
        }}</v-btn> -->
        <PrimaryButton
          className="primary-btn__blue dialog__footer__btn"
          :fullWidth="true"
          :text="$t(`visits.schedule.second.continue`)"
          size="large"
          :disabled="isButtonDisabled"
          @onClick="submit"
          :loading="buttonLoading"
        />
      </div>
    </v-card>
    <SuccessPayment @done="onDone" v-else />
  </v-dialog>
</template>

<script>
import { DateTime } from "luxon";
import { mapActions, mapGetters, mapMutations } from "vuex";

import SuccessPayment from "@/components/Checkout/SuccessPayement/Index";
import Stepper from "@/components/shared/Stepper";
import PrimaryButton from "@/components/uikit/PrimaryButton";
import Select from "@/components/uikit/Select";
import Textarea from "@/components/uikit/Textarea";
import { snackBarEventBus, snackBarEventName } from "@/eventBuses/snackBar.eventBus";

export default {
  name: "ScheduleDialog",
  props: {
    dialog: Boolean,
  },
  components: {
    Stepper,
    Select,
    Textarea,
    PrimaryButton,
    SuccessPayment,
  },
  data() {
    return {
      step: 1,
      texts: {
        _1: "View Details",
        _2: "Medical Information",
      },
      datepickerOpen: false,
      timeslotsOpen: false,
      buttonLoading: false,
    };
  },
  computed: {
    ...mapGetters(["uid"]),
    ...mapGetters("patients", ["practitionerOwnPatients"]),
    ...mapGetters("profileSettings", ["locale"]),
    ...mapGetters("checkout", ["slotId", "complaintDescription", "chiefComplaint", "visitDate", "patientId"]),
    ...mapGetters("slots", ["slotsArray"]),
    timeslot() {
      if (!this.slotId) return this.$t("visits.schedule.second.selectTime");
      const slot = this.slotsArray.find((sl) => sl.id === this.slotId);
      const startTime = DateTime.fromISO(slot.start).toFormat("t");
      const endTime = DateTime.fromISO(slot.end).toFormat("t");
      return `${startTime} - ${endTime}`;
    },
    date() {
      if (!this.visitDate) return this.$t("visits.schedule.second.selectDate");
      return DateTime.fromJSDate(new Date(this.visitDate)).setLocale(this.locale).toLocaleString({
        day: "numeric",
        month: "long",
        weekday: "long",
        year: "numeric",
      });
    },
    patientsOptions() {
      return this.practitionerOwnPatients.map(({ firstName, lastName, id }) => ({
        text: `${firstName} ${lastName}`,
        value: id,
      }));
    },
    chiefComplaints() {
      const chiefComplaints = this.$t("general.chiefComplaints");
      if (!chiefComplaints) {
        return [];
      }
      return Object.keys(chiefComplaints).map((key) => ({ text: chiefComplaints[key], value: key }));
    },
    slotsDates() {
      return this.slotsArray.map((slot) => DateTime.fromISO(slot.start).toJSDate());
    },
    slots() {
      return this.slotsArray
        .filter((slot) => {
          const startTime = DateTime.fromISO(slot.start).toFormat("yyyy-MM-dd");
          return startTime === this.visitDate;
        })
        .map((slot) => {
          const startTime = DateTime.fromISO(slot.start).toFormat("t");
          const endTime = DateTime.fromISO(slot.end).toFormat("t");
          return {
            text: `${startTime} - ${endTime}`,
            value: slot.id,
          };
        });
    },
    maxScheduleDate() {
      const dateTimes = this.slotsDates.map((date) => DateTime.fromJSDate(date));
      const date = dateTimes.length ? DateTime.max(...dateTimes).toJSDate() : new Date();
      return date.toISOString().substr(0, 10);
    },
    minScheduleDate() {
      const dateTimes = this.slotsDates.map((date) => DateTime.fromJSDate(date));
      const date = dateTimes.length ? DateTime.min(...dateTimes).toJSDate() : new Date();
      return date.toISOString().substr(0, 10);
    },
    availableDates() {
      return [...new Set(this.slotsArray.map((slot) => DateTime.fromISO(slot.start).day))];
    },
    isButtonDisabled() {
      return !this.slotId || !this.complaintDescription || !this.chiefComplaint || !this.visitDate;
    },
  },
  methods: {
    ...mapMutations("checkout", ["setStringFieldByName", "setVisitDate"]),
    ...mapActions("patients", ["getAllByPractitionerId"]),
    ...mapActions("slots", ["getPractitionerFreeSlots", "getSlotById"]),
    ...mapActions("patient", ["getPatientProfile"]),
    ...mapActions("checkout", ["practitionerRequestVisit", "clearAllFields"]),
    closeDialog() {
      this.$emit("on-close");
      this.clearAllFields();
      this.step = 1;
    },
    onPatientChange(value) {
      this.setStringFieldByName({ value, fieldName: "patientId" });
    },
    onChiefComplaintChange(value) {
      this.setStringFieldByName({ value, fieldName: "chiefComplaint" });
    },
    onVisitReasonChange(value) {
      this.setStringFieldByName({ value, fieldName: "complaintDescription" });
    },
    toggleDatepicker() {
      this.datepickerOpen = !this.datepickerOpen;
    },
    updateDate(newDate) {
      this.toggleDatepicker();
      this.setVisitDate(newDate);
    },
    updateSlotId(value) {
      this.setStringFieldByName({ value, fieldName: "slotId" });
    },
    allowedDates(val) {
      return this.availableDates.includes(DateTime.fromISO(val).day);
    },
    async submit() {
      this.$gtag.event("click", { method: "Open Schedule Dialog" });
      this.buttonLoading = true;
      try {
        await this.practitionerRequestVisit();
        await this.getPatientProfile(this.patientId);
        await this.getSlotById(this.slotId);
        this.step = "success";
      } catch (err) {
        if (err && err.response && err.response.status === 409) {
          snackBarEventBus.$emit(snackBarEventName, {
            message: this.$t("general.errors.slotConflict"),
            type: "error",
          });
          await this.getPractitionerFreeSlots(this.uid);
        }
        snackBarEventBus.$emit(snackBarEventName, {
          message: (err && err.response && err.response.data && err.response.data.message) || err,
          type: "error",
        });
      }
      this.buttonLoading = false;
    },
    onDone() {
      this.closeDialog();
    },
  },
  async mounted() {
    try {
      await this.getPractitionerFreeSlots(this.uid);
      await this.getAllByPractitionerId();
    } catch (err) {
      snackBarEventBus.$emit(snackBarEventName, {
        message: (err && err.response && err.response.data && err.response.data.message) || err,
        type: "error",
      });
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss">
.dialog {
  display: flex !important;
  flex-direction: column;
  align-items: flex-start;
  padding: 2rem;
  position: relative;

  img {
    display: inline-block;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    width: 100%;
    position: relative;

    &__title {
      font-style: normal;
      font-weight: 500;
      font-size: 20px;
      line-height: 24px;
      text-align: center;
      word-break: break-word !important;
      padding: 0;
    }
    &__close-icon {
      top: -12px;
      right: -12px;
      padding: 12px;
    }
  }

  &__stepper {
    width: 100%;
    border-bottom: none;
    height: 40px;
  }

  &__details {
    background-color: $primarylight1;
    border-radius: 4px;
    padding: 10px;
    margin-top: 24px;
    width: 100%;
    position: relative;

    & > div {
      margin: 0;
    }
    &__label {
      margin: 0 10px;
      font-size: 16px;
      color: $primaryblack2;
      opacity: 0.6;
    }
    &__text {
      font-size: 16px;
      color: $primaryblack2;
    }

    &__date-picker {
      position: absolute;
      z-index: 5;
      left: 50%;
      top: 35px;
      transform: translateX(-50%);
      box-shadow: 0px 18px 24px rgba(0, 0, 0, 0.14);
    }
    &__select-block {
      position: relative;
    }
    &__select {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      opacity: 0;
    }
  }

  &__select {
    width: 100%;
    font-size: 16px !important;
  }

  &__footer {
    width: 100%;
    margin-top: 24px;
    position: relative;

    &:before {
      content: "";
      position: absolute;
      left: -28px;
      top: 0;
      width: calc(100% + 56px);
      height: 1px;
      background-color: #d2d3e1;
    }

    &__btn {
      margin-top: 24px;
    }
  }
}
</style>
